import React from 'react';
import { theme } from 'antd';

export interface IBookingActionsRowProps {
  /** Looking, reading, downloading: Seguimiento, Chat, Factura, check-in/out. Nothing here changes anything. */
  neutral: React.ReactNode[];
  /** Cancelling and refunding. Real money, no undo. */
  risky?: React.ReactNode[];
}

/**
 * The actions at the foot of a booking, in two groups.
 *
 * They used to be one flat row in whatever order the branches happened to
 * push them, so "Devolver importe" -- which moves real money out of a real
 * account, with no undo -- sat between "Factura" and "Marcar no-show" looking
 * exactly like them. Reading the row gave no clue which of these you can
 * click to find something out and which you cannot take back.
 *
 * So the destructive ones are pulled out to the right, behind a divider and
 * a gap. Position, not just colour: colour alone is not a distinction
 * everybody can see, and on a busy row it is easy to miss.
 *
 * The divider is dropped when the row wraps to its own line -- a vertical
 * rule at the start of a line separates nothing.
 */
const BookingActionsRow: React.FC<IBookingActionsRowProps> = ({ neutral, risky }) => {
  const { token } = theme.useToken();
  const hasRisky = !!risky && risky.length > 0;

  if (neutral.length === 0 && !hasRisky) return null;

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, width: '100%' }}>
      {neutral}

      {hasRisky && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 8,
            // Pushed to the far end of whatever line it lands on, so the two
            // groups never read as one run of equivalent buttons.
            marginLeft: 'auto',
          }}
        >
          {neutral.length > 0 && (
            <span
              aria-hidden
              style={{
                width: 1,
                alignSelf: 'stretch',
                minHeight: 20,
                marginRight: 4,
                background: token.colorBorderSecondary,
              }}
            />
          )}
          {risky}
        </div>
      )}
    </div>
  );
};

export default BookingActionsRow;
